:host {
  display: block;

  /** @prop --select-item-background-color: Set the background color of the <anchor-select-item> */
  --select-item-background-color: var(--s-color-background-default-default);
  /** @prop --select-item-text-color: Set the text color of the <anchor-select-item> */
  --select-item-text-color: var(--s-color-text-contrast);
  /** @prop --select-item-border-color: Set the border color of the <anchor-select-item> */
  --select-item-border-color: var(--s-color-border-default);
  /** @prop --select-item-border-color-divider: Set the border color of the <anchor-select-item> divider */
  --select-item-border-color-divider: var(--s-color-border-divider);
  /** @prop --select-item-border-width: Set the border width of the <anchor-select-item> */
  --select-item-border-width: var(--p-size-static-0125);
  /** @prop --select-item-border-radius: Set the border radius of the <anchor-select-item> */
  --select-item-border-radius: 0;
  /** @prop --select-item-font-weight: Set the font weight of the <anchor-select-item> */
  --select-item-font-weight: var(--s-font-weight-paragraph-default);
  /** @prop --select-item-font-family: Set the font family of the <anchor-select-item> */
  --select-item-font-family: inherit;
  /** @prop --select-item-font-size: Set the font size of the <anchor-select-item> */
  --select-item-font-size: var(--s-size-text-default);
  /** @prop --select-item-line-height: Set the line height of the <anchor-select-item> */
  --select-item-line-height: var(--typography-line-height-default);
  /** @prop --select-item-line-height-inside: Set the line height inside of the <anchor-select-item> */
  --select-item-line-height-inside: var(--p-size-dynamic-300);
  /** @prop --select-item-padding: Set the padding of the <anchor-select-item> */
  --select-item-padding: calc(0.75rem - var(--select-item-border-width)) calc(1rem - var(--select-item-border-width));
  /** @prop --select-item-width: Set the width of the <anchor-select-item> */
  --select-item-width: calc(330px - calc(var(--select-item-border-width) * 2));
  /** @prop --select-item-cursor: Set the cursor of the <anchor-select-item> */
  --select-item-cursor: pointer;
  /** @prop --select-item-border-width-focus: Set the border width of the <anchor-select-item> when focused */
  --select-item-border-width-focus: var(--s-size-border-width-default);
  /** @prop --select-item-background-color-hover: Set the background color of the <anchor-select-item> when hovered */
  --select-item-background-color-hover: var(--s-color-background-default-default);
  /** @prop --select-item-border-color-divider-hover: Set the border color of the <anchor-select-item> divider when hovered */
  --select-item-border-color-divider-hover: var(--s-color-outline-default);
  /** @prop --select-item-border-color-hover: Set the border color of the <anchor-select-item> when hovered */
  --select-item-border-color-hover: var(--s-color-background-default-default);
  /** @prop --select-item-text-color-hover: Set the text color of the <anchor-select-item> when hovered */
  --select-item-text-color-hover: var(--s-color-text-default);
  /** @prop --select-item-border-color-pressed: Set the border color of the <anchor-select-item> when pressed */
  --select-item-border-color-pressed: var(--s-color-outline-default);
  /** @prop --select-item-border-color-divider-pressed: Set the border color of the <anchor-select-item> divider when pressed */
  --select-item-border-color-divider-pressed: var(--s-color-outline-default);
  /** @prop --select-item-background-color-pressed: Set the background color of the <anchor-select-item> when pressed */
  --select-item-background-color-pressed: var(--b-color-black);
  /** @prop --select-item-text-color-pressed: Set the text color of the <anchor-select-item> when pressed */
  --select-item-text-color-pressed: var(--s-color-text-on-dark-default);
  /** @prop --select-item-cursor-pressed: Set the cursor of the <anchor-select-item> when pressed */
  --select-item-cursor-pressed: wait;
  /** @prop --select-item-border-color-disabled: Set the border color of the <anchor-select-item> when disabled */
  --select-item-border-color-disabled: var(--s-color-border-divider);
  /** @prop --select-item-border-color-divider-disabled: Set the border color of the <anchor-select-item> divider when disabled */
  --select-item-border-color-divider-disabled: var(--s-color-border-divider);
  /** @prop --select-item-background-color-disabled: Set the background color of the <anchor-select-item> when disabled */
  --select-item-background-color-disabled: var(--s-color-background-default-disabled);
  /** @prop --select-item-text-color-disabled: Set the text color of the <anchor-select-item> when disabled */
  --select-item-text-color-disabled: var(--s-color-text-disabled);
  /** @prop --select-item-cursor-disabled: Set the cursor of the <anchor-select-item> when disabled */
  --select-item-cursor-disabled: not-allowed;

  // Default variables
  --background-color: var(--select-item-background-color);
  --text-color: var(--select-item-text-color);
  --border-color: var(--select-item-border-color);
  --border-divider: var(--select-item-border-color-divider);
  --border-width: var(--select-item-border-width);
  --border-radius: var(--select-item-border-radius);
  --font-weight: var(--select-item-font-weight);
  --font-family: var(--select-item-font-family);
  --font-size: var(--select-item-font-size);
  --line-height: var(--select-item-line-height);
  --line-height-inside: var(--select-item-line-height-inside);
  --padding: var(--select-item-padding);
  --width: var(--select-item-width);
  --cursor: var(--select-item-cursor);

  // Focus variables
  --border-focus: var(--select-item-border-width-focus);

  // Hover variables
  --background-hover: var(--select-item-background-color-hover);
  --divider-hover: var(--select-item-border-color-divider-hover);
  --border-hover: var(--select-item-border-color-hover);
  --text-hover: var(--select-item-text-color-hover);
  // --font-weight-hover: var(--typography-font-weight-semibold); // Waiting for implementation of token

  // Pressed variables
  --border-pressed: var(--select-item-border-color-pressed);
  --divider-pressed: var(--select-item-border-color-divider-pressed);
  --background-pressed: var(--select-item-background-color-pressed);
  // --background-pressed: var(--color-background-pressed); // Waiting for implementation of token
  --text-pressed: var(--select-item-text-color-pressed);
  --cursor-pressed: var(--select-item-cursor-pressed);

  // Disabled variables
  --border-disabled: var(--select-item-border-color-disabled);
  --divider-disabled: var(--select-item-border-color-divider-disabled);
  --background-disabled: var(--select-item-background-color-disabled);
  --text-disabled: var(--select-item-text-color-disabled);
  --cursor-disabled: var(--select-item-cursor-disabled);

  .anchor-c-select-item {
    // Reset styles
    outline: none;
    appearance: none;

    --select-item-line-height: var(--select-item-line-height-inside);

    width: var(--select-item-width);
    max-width: 100%;

    cursor: var(--select-item-cursor);
    background-color: var(--select-item-background-color);

    padding: var(--select-item-padding);
    border: var(--select-item-border-width) solid var(--select-item-border-color);
    border-radius: var(--select-item-border-radius);

    color: var(--select-item-text-color);
    font-family: var(--select-item-font-family);
    font-size: var(--select-item-font-size);
    font-weight: var(--select-item-font-weight);
    line-height: var(--select-item-line-height);

    &:hover,
    &:focus {
      --select-item-background-hover: var(--b-color-neutral-lighter);

      --select-item-background-color: var(--select-item-background-hover);
      --select-item-text-color: var(--select-item-text-hover);
    }

    &:focus,
    &:active {
      box-shadow: 0px 0px 0px var(--p-size-static-0125) var(--s-color-outline-default) inset;
    }
    &.selected {
      box-shadow: 0px 0px 0px var(--p-size-static-0125) var(--select-item-border-color) inset;
      --select-item-text-color: var(--s-color-text-default);
    }

    &:disabled {
      --select-item-border-color: var(--select-item-border-disabled);
      --select-item-border-divider: var(--select-item-border-color-divider-disabled);
      --select-item-background-color: var(--select-item-background-disabled);
      --select-item-text-color: var(--select-item-text-disabled);
      --select-item-cursor: var(--select-item-cursor-disabled);
    }

    &:not(.readonly):active {
      --select-item-border-color: var(--select-item-border-pressed);
      --select-item-border-divider: var(--select-item-divider-pressed);
      --select-item-background-color: var(--select-item-background-pressed);
      --select-item-text-color: var(--select-item-text-pressed);
      --select-item-cursor: var(--select-item-cursor-pressed);
    }

    .value,
    .description {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      color: var(--select-item-text-color);
      font-size: var(--select-item-font-size);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      width: 100%;
    }
    .top {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }
  }
}
